import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAndGetCSRFToken, getCSRFToken } from './utils/csrf';
import { patchFetchForMfa } from './utils/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    patchFetchForMfa();

    // Check if the current page load is a reload/refresh
    const isReload =
      (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]?.type === 'reload') ||
      (window.performance && window.performance.navigation && window.performance.navigation.type === 1);

    if (isReload) {
      // Invalidate MFA state on the server, then redirect to the verification screen
      axios.post('/api/auth/mfa/invalidate', {}, { withCredentials: true })
        .then(() => {
          window.location.href = '/mfa-verify';
        })
        .catch(() => {
          window.location.href = '/mfa-verify';
        });
    } else {
      // Standard load for direct navigation or normal app operations
      axios.get('/api/auth/user', { withCredentials: true })
        .then(res => setUser(res.data))
        .catch(() => setUser(null))
        .finally(() => setLoading(false));
    }
  }, []);

  // Listen for global MFA-required events (emitted by axios interceptor)
  useEffect(() => {
    const onMfaRequired = () => {
      // mark user as needing MFA re-verification
      setUser(prev => (prev ? { ...prev, mfa_verified: false } : prev));
      // navigate to SPA route without full reload
      try {
        window.history.pushState({}, '', '/mfa-verify');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (e) {
        window.location.href = '/mfa-verify';
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      if (reason?.isMfaRequired || reason?.response?.data?.mfa_required) {
        event.preventDefault();
      }
    };

    window.addEventListener('mfa_required', onMfaRequired);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('mfa_required', onMfaRequired);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const refreshUser = async () => {
    const response = await axios.get('/api/auth/user', { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const normalizeRole = (userData) => {
    if (!userData) return '';
    return userData.role_name || userData.role || userData.user_metadata?.role || '';
  };

  const login = async (email, password, code, csrfToken) => {
    try {
      const startRes = await axios.post('/api/auth/login/start', { email }, {
        headers: { 'X-CSRFToken': csrfToken },
        withCredentials: true,
      });

      if (startRes.data.requires_password) {
        if (!password) {
          return { success: false, requiresPassword: true };
        }
        const passwordRes = await axios.post('/api/auth/login/password', { email, password }, {
          headers: { 'X-CSRFToken': csrfToken },
          withCredentials: true,
        });

        if (passwordRes.data.requires_mfa_setup) {
          try {
            const userData = await refreshUser();
            return { success: true, requiresMfaSetup: true, email, role: normalizeRole(userData) };
          } catch (_) {
            return { success: false, message: 'Unable to complete login. Please try again.' };
          }
        }

        try {
          const userData = await refreshUser();
          return { success: true, role: normalizeRole(userData) };
        } catch (_) {
          return { success: false, message: 'Unable to complete login. Please try again.' };
        }
      }

      if (startRes.data.requires_mfa_verify) {
        if (!code) {
          return { success: false, requiresMfaVerify: true };
        }
        const verifyRes = await axios.post('/api/auth/mfa/verify', { email, code }, {
          headers: { 'X-CSRFToken': csrfToken },
          withCredentials: true,
        });

        if (verifyRes.status === 200) {
          const res = await axios.get('/api/auth/user', { withCredentials: true });
          setUser(res.data);
          return { success: true, role: normalizeRole(res.data) };
        }
      }

      return { success: false, message: 'Unable to determine login flow.' };
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.message || 'Login failed.';
      return { success: false, message };
    }
  };

  const logout = async () => {
    await fetchAndGetCSRFToken();
    const csrfToken = getCSRFToken();
    await axios.post('/api/auth/logout', {}, {
      headers: { 'X-CSRFToken': csrfToken },
      withCredentials: true,
    });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
